/**
 * An installed fill, hydrated and mounted (RFC 0284 S6a, S6c).
 *
 * Extracted when the HEADER became the second slot to go through the registry.
 * Fetching the module out of its view document, saying so while it is in
 * flight, and mounting it are the same three steps for every slot, and the
 * sidebar had them written inline in `WorldShell`. Copying them per slot would
 * have meant three places to fix the next time a fill failed to arrive.
 *
 * The three steps are `useContribution` and `ContributionWaiting` now, shared
 * with the thread renderer and the host-realm editor. What is left here is the
 * one thing a SLOT does differently: it keys the mount by app id, so switching
 * layouts tears the old module down and boots the new one rather than handing
 * a mounted module a different container.
 *
 * A COMPONENT, not a helper, because the middle step is a hook. `useDocument`
 * cannot be called conditionally, so the shell was hydrating the sidebar's view
 * document whether or not the sidebar was installed. Owning the hook here means
 * the document is only opened when something installed is actually being drawn.
 */

import { DocumentView } from '../../features/documents/DocumentView'
import { Mount } from '../../sdk/host/Mount'
import { useContribution } from '../../sdk/host/useContribution'
import { useAppSettings } from '../../sdk/useAppSettings'
import { ContributionWaiting } from './ContributionWaiting'
import type { SlotApp } from './slots'

export function InstalledSlot({
  app,
  slot,
  projectId,
  sessionId,
}: {
  app: SlotApp
  slot?: 'sidebar' | 'sessionHeader' | 'header' | 'composer'
  projectId?: string
  sessionId?: string
}) {
  if (
    app.realm !== 'host' &&
    (slot === 'sidebar' || slot === 'sessionHeader')
  ) {
    if (!projectId || !app.viewDocId) return null
    return (
      <div className={slot === 'sidebar' ? 'h-full min-h-0' : 'h-9 min-w-24'}>
        <DocumentView
          file={{
            id: app.id,
            appId: app.id,
            viewDocId: app.viewDocId,
            title: app.name,
            path: app.name,
            mime: 'text/html',
          }}
          projectId={projectId}
          surface={slot}
          surfaceContext={{
            projectId,
            ...(sessionId ? { sessionId } : {}),
          }}
        />
      </div>
    )
  }
  return <HostInstalledSlot app={app} />
}

function HostInstalledSlot({ app }: { app: SlotApp }) {
  const { code, state } = useContribution(app.viewDocId)
  // ABOVE the branch, because a hook cannot be conditional.
  const settings = useAppSettings(app)

  if (state !== 'ready') {
    return (
      <ContributionWaiting
        name={app.name}
        what="the fill's"
        recovery="Settings (⌘,) then Built in gets your chrome back."
      />
    )
  }
  return <Mount key={app.id} code={code} title={app.name} settings={settings} />
}
